import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import BlockRenderer from '../components/cms/BlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders a CMS page based on the current URL path. Works as a catch-all
 * for any slug under PublicLayout (e.g. /, /about, /services/physical).
 */
export default function DynamicPage() {
  const params = useParams();
  const location = useLocation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Compute slug: if useParams() carries `*` (splat) use it; else the URL path.
  const slug = (params['*'] || params.slug || location.pathname.replace(/^\//, '') || 'home')
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setDoc(null);
    const target = slug || 'home';
    axios.get(`${API}/public/cms/pages/${target}`)
      .then((res) => { if (!cancelled) setDoc(res.data); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (doc?.title) document.title = `${doc.title} | Elite Home Rehab`;
    if (doc?.meta_description) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', 'description');
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', doc.meta_description);
    }
  }, [doc]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Loading…</div>;
  }
  if (notFound) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-semibold mb-3">Page not found</h1>
          <p className="text-muted-foreground mb-6">The page you're looking for doesn't exist or hasn't been published yet.</p>
          <Link to="/" className="px-4 py-2 rounded-md bg-[var(--site-primary,#1a3a5c)] text-white font-semibold hover:opacity-90">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cms-page" data-testid="cms-page-root" data-slug={doc.slug}>
      {(doc.blocks || []).map((block) => (
        <BlockRenderer key={block.id} block={block} />
      ))}
    </div>
  );
}
